import { ReactNode, useCallback, FC } from 'react';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';

import { WELCOME_STORAGE_KEY } from 'constants/common';

interface IWelcomeModalProps {
  open: boolean;
  // eslint-disable-next-line no-unused-vars
  setOpen: (open: boolean) => void;
}

interface IInfoItemProps {
  prefix?: ReactNode;
}

const defaultInfoItemPrefix = (
  <Typography
    component="span"
    color="secondary.main"
    fontWeight={900}
    fontSize="h4.fontSize"
    lineHeight={0.35}
  >
    &bull;
  </Typography>
);

const InfoItem: FC<IInfoItemProps> = ({
  children,
  prefix = defaultInfoItemPrefix,
}) => (
  <Stack direction="row" fontWeight="bold" py={1}>
    {prefix}

    <Typography
      pl={[1, undefined, 3]}
      fontWeight="bold"
      color="info.main"
      variant="body1"
      lineHeight={1.2}
    >
      {children}
    </Typography>
  </Stack>
);

const infosTranslationKeys = [
  'WelcomeModal.Info1',
  'WelcomeModal.Info2',
  'WelcomeModal.Info3',
  'WelcomeModal.Info4',
  'WelcomeModal.Info5',
];

const WelcomeModal: FC<IWelcomeModalProps> = ({ open, setOpen }) => {
  const { t: translate } = useTranslation();

  const handleClose = useCallback(
    (_, reason?: 'escapeKeyDown' | 'backdropClick') => {
      if (reason !== 'backdropClick') {
        localStorage.setItem(WELCOME_STORAGE_KEY, 'true');
        setOpen(false);
      }
    },
    []
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="welcome-dialog-title"
      aria-describedby="welcome-dialog-description"
      PaperProps={{
        sx: {
          p: [1, undefined, undefined, 2],
          minWidth: [undefined, undefined, undefined, 1 / 3],
          minHeight: [undefined, undefined, undefined, 500],
        },
      }}
      disableEscapeKeyDown
    >
      <DialogTitle id="welcome-dialog-title">
        {translate('Common.Welcome')}
      </DialogTitle>
      <DialogContent
        sx={{
          py: [2.5 / 3, undefined, 2.5],
          px: [1, undefined, 3],
        }}
      >
        <DialogContentText id="welcome-dialog-description">
          {translate('WelcomeModal.Description', {
            appContext: process.env.REACT_APP_VAULT_CONTEXT,
          })}
        </DialogContentText>

        <Stack py={[1, undefined, 3]}>
          {infosTranslationKeys.map((translationKey) => (
            <InfoItem key={translationKey}>
              {translate(translationKey, {
                appContext: process.env.REACT_APP_VAULT_CONTEXT,
              })}
            </InfoItem>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{ width: '70%', py: 2, borderRadius: '0.5rem' }}
        >
          {translate('Common.I_Understood')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeModal;
